/*
 * GALPÓN — Memoria de posición de navegación (PILA multi-nivel).
 * ---------------------------------------------------------------------------
 * Devuelve al usuario a la POSICIÓN EXACTA de scroll de la vista a la que vuelve,
 * en CUALQUIER nivel de la jerarquía colección → familia → producto, y por
 * CUALQUIER vía de retorno:
 *   - Botón "atrás" del navegador (desanda paso a paso: PDP → familia → colección).
 *   - Botones de la PDP (breadcrumb / eyebrow / "Ver más en…") que saltan DIRECTO
 *     a la colección madre (se saltan la familia).
 *
 * MODELO — una PILA en sessionStorage (NO un slot único). Cada entrada es
 * { url, scrollY }, indexada por la URL COMPLETA de la vista (pathname + search,
 * incluido ?familia=X). La restauración busca en la pila la entrada cuya url
 * coincide con la URL ACTUAL → restaura ESE scroll. Así:
 *   - /collections/muebles?familia=X  → restaura el scroll DENTRO de la familia.
 *   - /collections/muebles            → restaura el scroll de la COLECCIÓN.
 * La URL es el desambiguador: familia y colección son URLs distintas → sus scrolls
 * nunca se confunden, sin importar por qué vía se llegó. Si una URL se repite en la
 * pila (ida y vuelta), gana la entrada MÁS RECIENTE (la última guardada) — correcto
 * el caso normal.
 *
 * Por qué NO history.state: Horizon lo PISA desde facets.js (pushState con
 * {urlParameters}), variant-picker.js (replaceState {}) y paginated-list.js
 * (pushState ''). Guardar el scroll ahí lo borraría al filtrar/paginar/cambiar
 * variante. sessionStorage es intocable por Horizon.
 *
 * sessionStorage (NO local): por-pestaña y efímero. Dos pestañas no se pisan;
 * cerrar la pestaña olvida. Correcto para "memoria de navegación de esta sesión".
 *
 * Timing de restauración: el grid de colección colapsa familias en su init
 * (gd-collection-grid.js) → la ALTURA del documento cambia DESPUÉS de cargar.
 * Restaurar antes de eso falla (el browser clampea el scroll a una altura que aún
 * no es la definitiva). Esperamos la señal `gd:grid-ready` (o la clase .gd-grid-ready
 * si ya pasó) → altura definitiva → recién ahí restauramos.
 * (Antes emitíamos `gd:scroll-restored` para coordinar con el velo de carga; ese velo fue
 *  ELIMINADO en jul 2026 —ver `theme.liquid`— y la señal ya no la escucha nadie. Se quitó.)
 *
 * Carga: en TODAS las páginas (guarda al salir de un listado; restaura al entrar).
 */
(function () {
  'use strict';

  const STACK_KEY = 'gd:nav-stack';
  const PENDING_KEY = 'gd:nav-restore-pending'; // '1' → la próxima vista de listado debe restaurar
  const MAX_ENTRIES = 30; // techo de la pila (navegación larga) — evita crecer sin límite

  // NOTA: NO tocamos history.scrollRestoration (lo dejamos en 'auto', el default). Ponerlo en
  // 'manual' es GLOBAL (afecta TODO el sitio) → dejaría home/PDP/políticas sin restauración de
  // scroll al volver atrás. Nuestro control es por-vista: hacemos scrollTo explícito DESPUÉS
  // del reflow del grid, que gana a la restauración automática del navegador en las colecciones
  // (corremos más tarde). En el resto de páginas, el navegador sigue restaurando solo.

  function readStack() {
    try { return JSON.parse(sessionStorage.getItem(STACK_KEY) || '[]') || []; } catch { return []; }
  }
  function writeStack(stack) {
    try { sessionStorage.setItem(STACK_KEY, JSON.stringify(stack.slice(-MAX_ENTRIES))); } catch {}
  }

  // URL canónica de una vista para indexar la pila: pathname + search RELEVANTE.
  // Conservamos ?cat= (filtro) y ?familia= (nivel) porque definen la VISTA; descartamos
  // params efímeros de Shopify/marketing que no cambian la posición navegable.
  function viewKey(pathname, search) {
    let keep = '';
    try {
      const sp = new URLSearchParams(search || '');
      const out = new URLSearchParams();
      for (const k of ['cat', 'familia', 'sort_by', 'q', 'page']) {
        if (sp.has(k)) out.set(k, sp.get(k));
      }
      const s = out.toString();
      keep = s ? '?' + s : '';
    } catch { keep = search || ''; }
    return pathname + keep;
  }
  function currentViewKey() { return viewKey(location.pathname, location.search); }

  // Guardar el scroll de la vista ACTUAL en la pila, bajo su viewKey. Si ya había una
  // entrada para esa url, la reemplaza (la más reciente gana) y la mueve al tope.
  function pushCurrentScroll(scrollYOverride) {
    const key = currentViewKey();
    const scrollY = typeof scrollYOverride === 'number' ? scrollYOverride : Math.round(window.scrollY);
    const stack = readStack().filter((e) => e.url !== key);
    stack.push({ url: key, scrollY: scrollY });
    writeStack(stack);
  }

  // Buscar (sin consumir) el scroll guardado para una url. La MÁS RECIENTE gana.
  function peekScroll(key) {
    const stack = readStack();
    for (let i = stack.length - 1; i >= 0; i--) {
      if (stack[i].url === key) return stack[i].scrollY;
    }
    return null;
  }

  // ── 1. GUARDAR al SALIR de una vista de listado hacia otra URL ───────────────
  // Guardamos el scroll de la vista actual ANTES de irnos, para poder volver a él.
  // Dos disparadores complementarios:
  //   (a) click en un link a /products/ desde un listado → guarda el scroll del listado.
  //   (b) pagehide (cualquier salida real: link, atrás, submit) → snapshot del scroll
  //       actual bajo la url actual. Red que atrapa TODA salida, no solo el click.
  const isListing = (p) => /^\/(collections|search)/.test(p);

  document.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const link = e.target.closest('a[href*="/products/"]');
    if (!link) return;
    if (!isListing(location.pathname)) return; // solo desde un listado con scroll que valga la pena

    // Si el click ocurre DENTRO de una familia (html.gd-in-family), la url actual ya lleva
    // ?familia=X (la puso el drill con pushState) → viewKey la captura y guardamos el scroll
    // DE LA FAMILIA bajo esa url. La entrada de la COLECCIÓN madre (sin ?familia) ya quedó en
    // la pila cuando se hizo el drill (ver más abajo, evento gd:family-enter). Así al volver:
    //   atrás #1 → url con ?familia → restaura scroll de familia.
    //   atrás #2 → url sin familia  → restaura scroll de colección.
    pushCurrentScroll();
  }, true); // capture: corre antes que el handler del drill

  // pagehide: red de seguridad para CUALQUIER salida (atrás del navegador, submit, etc.).
  // Guarda el scroll de la vista actual si es un listado. No molesta en otras páginas.
  window.addEventListener('pagehide', () => {
    if (isListing(location.pathname)) pushCurrentScroll();
  });

  // El drill de familia (gd-collection-grid.js) avisa cuando entra/sale de una familia, para
  // que guardemos el scroll de la COLECCIÓN (vista A) bajo su url sin ?familia — así queda en
  // la pila ANTES de que el usuario entre a un producto desde la familia. Sin esto, al volver
  // dos veces (PDP→familia→colección) el scroll de la colección no estaría guardado.
  document.addEventListener('gd:family-enter', (e) => {
    const collScroll = e.detail && typeof e.detail.collectionScroll === 'number'
      ? e.detail.collectionScroll : null;
    if (collScroll == null) return;
    // guardar el scroll de la COLECCIÓN bajo su url SIN ?familia (la url madre).
    const madreKey = viewKey(location.pathname, stripFamilia(location.search));
    const stack = readStack().filter((en) => en.url !== madreKey);
    stack.push({ url: madreKey, scrollY: Math.round(collScroll) });
    writeStack(stack);
  });

  function stripFamilia(search) {
    try {
      const sp = new URLSearchParams(search || '');
      sp.delete('familia');
      const s = sp.toString();
      return s ? '?' + s : '';
    } catch { return search || ''; }
  }

  // ── 2. RESTAURAR al ENTRAR a una vista de listado ────────────────────────────
  // Al cargar/volver a un listado, si hay scroll guardado para ESTA url exacta, lo
  // restauramos. Se dispara tanto por navegación real (volver desde PDP) como por el
  // popstate same-document del drill (ese lo maneja gd-collection-grid, pero el scroll
  // de la colección lo restaura este módulo vía la señal). Requiere que la altura del
  // grid esté definitiva (gd:grid-ready).
  // Acelerador del velo ciego (anti-flick, jul 2026): en listados, el velo NO se revela a
  // tiempo fijo — lo revela este módulo cuando la vista ya está en posición (o cuando se
  // confirma que no hay nada que restaurar). Solo ADELANTA el fade: el piso CSS de 850ms
  // del velo (theme.liquid) revela igual aunque este código muera.
  function veilGo(minMs) {
    if (window.gdVeilGo) window.gdVeilGo(minMs);
  }

  // Espera el grid DEFINITIVO (colapsado por familias) y recién ahí revela — con piso
  // mínimo visible para que el velo sea PERCEPTIBLE. Si nada de esto corre, el piso CSS
  // del velo (2.5s) revela igual.
  function veilGoWhenGridReady(minMs) {
    if (document.querySelector('.gd-grid-ready')) { veilGo(minMs); return; }
    // ⚠️ El timeout corto SOLO donde NO existe el motor de colección (/search, que nunca
    // emite gd:grid-ready). En una COLECCIÓN real, revelar por timeout ANTES del grid-ready
    // funde el velo sobre un grid AÚN OCULTO → papel vacío → grid de golpe (cazado por F:
    // en móvil DCL+colapso tardan >1.2s). En colección los fallbacks son el piso CSS del
    // velo (2.5s) + el failsafe del grid (1.5s, ahora con fade).
    const hasEngine = !!document.querySelector('main[data-template="collection"] .product-grid');
    let done = false;
    const onReady = () => { if (done) return; done = true; document.removeEventListener('gd:grid-ready', onReady); veilGo(minMs); };
    document.addEventListener('gd:grid-ready', onReady);
    if (!hasEngine) setTimeout(() => { if (!done) onReady(); }, 1200);
  }

  function restoreForCurrentView() {
    const key = currentViewKey();
    const scrollY = peekScroll(key);
    if (scrollY == null) { veilGoWhenGridReady(350); return; } // nada guardado → revelar con el grid listo

    const restore = () => {
      // D-coleccion-1: con cards livianas, la ALTURA FINAL del grid llega recién al
      // hidratarse (las pendientes están display:none hasta entonces) → restaurar antes
      // clampearía el scroll. Esperar la hidratación si el motor la expone; si el fetch
      // falla, la promesa resuelve IGUAL (el motor emite la señal en el catch — nunca
      // se cuelga). Páginas sin motor: gdGridHydration no existe → paso directo.
      const hydrated = window.gdGridHydration ? window.gdGridHydration() : Promise.resolve();
      hydrated.then(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        // ANTI-SALTO: el grid quedó oculto (.gd-grid-restoring) hasta este momento para que el
        // usuario NO vea "caigo arriba → salto". Ya posicionado, lo descorremos al frame siguiente.
        requestAnimationFrame(() => {
          const g = document.querySelector('.product-grid.gd-grid-restoring');
          if (g) g.classList.remove('gd-grid-restoring');
          // v13: revelar el main completo YA posicionado (fade 320ms vía transition CSS)
          document.documentElement.classList.remove('gd-returning');
          // YA en posición y con el grid visible → que el velo se descorra, pero con
          // presencia MÍNIMA de 300ms desde la navegación: si el restore fue relámpago,
          // sin este piso el velo es subliminal y el retorno se siente "de golpe"
          // (feedback de F). 300ms + fade 480ms = transición percibida, no lentitud.
          veilGo(350);
        });
      })));
    };

    if (document.querySelector('.gd-grid-ready')) {
      restore();
    } else {
      let done = false;
      const onReady = () => { if (done) return; done = true; document.removeEventListener('gd:grid-ready', onReady); restore(); };
      document.addEventListener('gd:grid-ready', onReady);
      // red de seguridad SOLO sin motor de colección (/search): en colección, restaurar por
      // timeout antes del colapso posiciona MAL (altura no definitiva) y revela temprano.
      const hasEngine2 = !!document.querySelector('main[data-template="collection"] .product-grid');
      if (!hasEngine2) setTimeout(() => { if (!done) onReady(); }, 1200);
    }
  }

  // ── 3. BOTONES de la PDP (breadcrumb / eyebrow / "Ver más en…") ───────────────
  // Saltan DIRECTO a la colección madre (se saltan la familia). Marcamos pending y dejamos
  // que la navegación real ocurra: al cargar la colección, restoreForCurrentView encuentra el
  // scroll de esa url (guardado al hacer el drill o al entrar a la familia) y restaura. Como
  // el destino es la url madre SIN ?familia, restaura el scroll de la COLECCIÓN — correcto: el
  // botón lleva a la colección, no a la familia.
  document.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const link = e.target.closest('[data-gd-return]');
    if (!link) return;
    // el href del botón ya apunta a la madre (url sin ?familia). No forzamos nada: la
    // restauración por-url en el destino hace el trabajo. Solo marcamos pending para que la
    // colección destino intente restaurar aunque venga por navegación fresca.
    try { sessionStorage.setItem(PENDING_KEY, '1'); } catch {}
    // guardar el scroll actual (por si el usuario estaba scrolleado en la PDP; inocuo).
  }, false);

  // ── 4. SWAP DE VERSIÓN en la PDP: no afecta la pila (la PDP no es un listado) ──
  // (El sistema viejo movía rec.to al cambiar de variante. Con la pila indexada por url de
  // LISTADO, el swap de variante en la PDP no toca ninguna entrada de listado → nada que hacer.)

  // ── Ejecutar la restauración si estamos en un listado ────────────────────────
  function maybeRestore() {
    if (!isListing(location.pathname)) return;
    // ¿Estás VOLVIENDO de una PDP? Doble señal (la misma que usa gd-collection-grid):
    // (a) flag pending = botón "volver" de la PDP; (b) referrer es /products/ = atrás del
    // navegador SIN bfcache. Solo entonces restauramos el scroll guardado.
    // ⚠️ ANTES restaurábamos ante CUALQUIER coincidencia de URL en la pila — un click
    // FRESCO del menú a una colección YA VISITADA encontraba el scroll viejo y entraba
    // al camino lento (grid + hidratación + reposicionar ≈ 3s) → el piso del velo (4s)
    // moría en el medio → "colección de golpe la mayoría de las veces" (cazado por F).
    // Y era UX incorrecta: entrar por el menú debe partir ARRIBA, no en el scroll viejo.
    let returning = false;
    try {
      returning = sessionStorage.getItem(PENDING_KEY) === '1';
      if (!returning && document.referrer) {
        returning = /\/products\//.test(new URL(document.referrer).pathname);
      }
    } catch {}
    try { sessionStorage.removeItem(PENDING_KEY); } catch {}
    if (returning) {
      restoreForCurrentView();
    } else {
      // entrada fresca: sin restore. Revelar el velo apenas el grid esté definitivo
      // (rápido: no espera hidratación) — el mismo camino que una colección nunca visitada.
      veilGoWhenGridReady(250);
    }
  }

  // Arranque ROBUSTO (mismo patrón que gd-collection-grid, ver harness "colección pegada
  // en Safari"): el listener de DOMContentLoaded puede perderse en WebKit → tres caminos
  // redundantes, idempotentes por flag.
  let ranRestore = false;
  function startRestore() {
    if (ranRestore) return;
    ranRestore = true;
    maybeRestore();
  }
  if (document.readyState !== 'loading') startRestore();
  document.addEventListener('DOMContentLoaded', startRestore);
  window.addEventListener('load', startRestore);

  // popstate: volver por el atrás del navegador a un listado que se restaura same-document
  // (el caso familia↔colección lo maneja gd-collection-grid abriendo/cerrando la vista; acá
  // restauramos el scroll del nivel destino, buscando por la url YA cambiada por el browser).
  window.addEventListener('popstate', () => {
    if (!isListing(location.pathname)) return;
    // el grid ya está montado (misma página) → restaurar directo tras el reflow del cambio de vista.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const scrollY = peekScroll(currentViewKey());
      if (scrollY != null) window.scrollTo({ top: scrollY, behavior: 'instant' });
    }));
  });
})();
